<template>
  <div>
    <div class="page-header"><div class="page-icon">📥</div><h2>进货记录</h2></div>

    <!-- 日期范围 + 搜索 -->
    <div class="filter-bar">
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        value-format="YYYY-MM-DD"
        :clearable="false"
        style="width: 360px;"
        @change="handleDateChange"
      ></el-date-picker>

      <el-input
        v-model="searchQuery"
        placeholder="搜索商品名称或备注关键字"
        prefix-icon="el-icon-search"
        clearable
        style="width: 300px; margin-left: 12px;"
      ></el-input>
    </div>

    <!-- 包裹表格的卡片 -->
    <div v-if="!loading">
      <el-card class="box-card">
        <el-table :data="paginatedPurchases" stripe>
          <el-table-column label="进货日期">
            <template #default="{ row }">
              {{ formatDate(row.transaction_date) }}
            </template>
          </el-table-column>

          <!-- 商品详情列，显示每条总价、总数量和总金额 -->
          <el-table-column label="商品详情">
            <template #default="{ row }">
              <div>
                <div v-for="(item, index) in row.product_details" :key="index">
                  {{ item }}
                </div>
                <div class="total-amount">
                  本次进货总数量: {{ row.total_quantity }} 条, 总金额: {{ row.total_amount }} 元
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="备注">
            <template #default="{ row }">
              <div v-if="isEditing(row)">
                <el-input
                  v-model="row.remark"
                  placeholder="输入备注"
                  clearable
                  @blur="handleBlur(row)"
                ></el-input>
              </div>
              <div v-else @click="handleClick(row)">
                <span>{{ row.remark || '点击添加备注' }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120">
            <template #default="{ row }">
              <el-button
                type="danger"
                size="small"
                @click="confirmUndo(row)"
                :disabled="isUndoing[row.transaction_date]"
              >
                {{ isUndoing[row.transaction_date] ? "撤回中..." : "撤回" }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <!-- 分页 -->
      <el-pagination
        v-show="totalItems > pageSize"
        :current-page="currentPage"
        :page-size="pageSize"
        :total="totalItems"
        layout="prev, pager, next, jumper, ->, total"
        @current-change="handlePageChange"
        style="margin-top: 20px; text-align: center;"
      ></el-pagination>
    </div>
    <div v-else style="text-align: center; padding: 20px;">加载中...</div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import axios from 'axios';
import API_BASE from '@/api';
import { ElMessage, ElMessageBox } from 'element-plus';
import dayjs from 'dayjs';
import { debounce } from 'lodash';

const purchases = ref([]);        // 当前日期范围内的进货记录
const searchQuery = ref('');      // 搜索框的输入
const currentPage = ref(1);       // 当前页码
const pageSize = ref(17);         // 每页显示条数
const totalItems = ref(0);        // 进货记录总数
const editingRow = ref(null);     // 存储正在编辑的行
const isUndoing = ref({});        // 记录正在撤回的订单
const loading = ref(false);       // 加载状态

// 日期范围（默认近 30 天）
const dateRange = ref([
  dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
  dayjs().format('YYYY-MM-DD')
]);

// 处理日期变化
const handleDateChange = () => {
  currentPage.value = 1;
  getPurchases();
};

// 防抖处理搜索输入
const debouncedSearch = debounce(() => {
  // 空实现，依赖 computed 自动更新
}, 300);

watch(searchQuery, () => {
  debouncedSearch();
});

// 确认撤回操作
const confirmUndo = (row) => {
  ElMessageBox.confirm(
    '确定要撤回该笔交易吗？',
    '提示',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  )
    .then(() => {
      handleUndo(row);
    })
    .catch(() => {
      // 用户取消操作，不做任何处理
    });
};

// 撤回交易
const handleUndo = async (row) => {
  try {
    const formattedDate = dayjs(row.transaction_date).format("YYYY-MM-DD HH:mm:ss");
    isUndoing.value[row.transaction_date] = true;

    const response = await axios.post(`${API_BASE}/api/transactions/undo`, {
      transaction_date: formattedDate
    });

    if (response.data.success) {
      ElMessage.success("整笔交易已撤回！");
      await getPurchases();
    } else {
      ElMessage.error("撤回失败：" + response.data.message);
    }
  } catch (error) {
    // 优先用后端返回的中文 message，否则才显示 axios 技术消息
    const backendMsg = error?.response?.data?.message;
    ElMessage.error(backendMsg ? `撤回失败：${backendMsg}` : `请求出错：${error.message}`);
  } finally {
    isUndoing.value[row.transaction_date] = false;
  }
};

// 判断是否是当前正在编辑的行
const isEditing = (row) => editingRow.value === row;

// 失去焦点时，保存备注并切换为文本模式
const handleBlur = async (row) => {
  editingRow.value = null;
  await updateRemark(row);
};

// 点击文本时，切换为编辑模式
const handleClick = (row) => {
  editingRow.value = row;
};

// 获取进货记录并计算总价、总数量和总金额
const getPurchases = async () => {
  loading.value = true;
  try {
    const [start, end] = dateRange.value;
    const response = await axios.get(`${API_BASE}/api/purchases`, {
      params: { start, end }
    });
    if (response.data.success) {
      purchases.value = response.data.purchases.map(item => {
        let totalAmount = 0;
        let totalQuantity = 0;
        const details = item.product_details.split("\n").map(detail => {
          const priceMatch = detail.match(/单价: (\d+\.\d{2}) 元/);
          const quantityMatch = detail.match(/数量: (\d+)/);
          if (priceMatch && quantityMatch) {
            const price = parseFloat(priceMatch[1]);
            const quantity = parseInt(quantityMatch[1], 10);
            const total = price * quantity;
            totalAmount += total;
            totalQuantity += quantity;
            return `${detail}, 金额: ${total.toFixed(1)} 元`;
          }
          return detail;
        });
        return {
          ...item,
          transaction_date: dayjs(item.transaction_date).toISOString(),
          product_details: details,
          total_amount: totalAmount.toFixed(1),
          total_quantity: totalQuantity
        };
      });
      totalItems.value = response.data.purchases.length;
    } else {
      ElMessage.error('获取进货记录失败：' + response.data.message);
    }
  } catch (error) {
    ElMessage.error('请求出错：' + error.message);
  } finally {
    loading.value = false;
  }
};

// 更新备注
const updateRemark = async (row) => {
  try {
    const formattedDate = dayjs(row.transaction_date).format('YYYY-MM-DD HH:mm:ss');
    const response = await axios.post(`${API_BASE}/api/purchases/update-remark`, {
      transaction_date: formattedDate,
      remark: row.remark,
    });

    if (response.data.success) {
      ElMessage.success("备注更新成功！");
    } else {
      ElMessage.error("备注更新失败：" + response.data.message);
    }
  } catch (error) {
    ElMessage.error("请求出错：" + error.message);
  }
};

// 格式化日期的函数
const formatDate = (date) => {
  if (!date) return "无效日期";
  return dayjs(date).format("YYYY-MM-DD HH:mm:ss");
};

// 过滤进货记录（商品名称 + 备注）
const filteredPurchases = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return purchases.value;

  return purchases.value.filter((purchase) => {
    // 1) 商品详情命中
    const productHit = (purchase.product_details || []).some((detail) =>
      String(detail).toLowerCase().includes(q)
    );
    if (productHit) return true;
    // 2) 备注命中
    const remark = purchase.remark;
    if (remark && String(remark).toLowerCase().includes(q)) return true;
    return false;
  });
});

// 搜索后重置到第 1 页
watch(filteredPurchases, () => {
  currentPage.value = 1;
});

// 分页后的进货记录
const paginatedPurchases = computed(() => {
  const startIndex = (currentPage.value - 1) * pageSize.value;
  const endIndex = currentPage.value * pageSize.value;
  return filteredPurchases.value.slice(startIndex, endIndex);
});

// 处理页码变化
const handlePageChange = (page) => {
  currentPage.value = page;
};

// 页面加载时获取数据
getPurchases();
</script>

<style scoped>
.filter-bar {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.box-card {
  margin-bottom: 16px;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  border: 1px solid var(--border-lighter);
}

.total-amount {
  margin-top: 10px;
  font-weight: 600;
  color: var(--primary);
  font-size: 13px;
}
</style>